import React from 'react';
import { FormattedMessage, FormattedDate, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_TIME,
  DATE_TYPE_DATETIME,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LINE_ITEM_FIXED,
  propTypes,
} from '../../util/types';
import { subtractTime, isDST, isSameDay } from '../../util/dates';

import css from './OrderBreakdown.module.css';

const DSTMaybe = props => {
  const { startDate, endDate, isStart, dateType, timeZone } = props;
  const isDayTimeRange = dateType === DATE_TYPE_DATETIME;
  if (!isDayTimeRange) {
    return null;
  }

  const isStartInDST = isDST(startDate, timeZone);
  const isEndInDST = isDST(endDate, timeZone);
  const isDSTChanged = isStartInDST !== isEndInDST;
  const showDSTMsgForStart = isDSTChanged && isStart && isStartInDST;
  const showDSTMsgForEnd = isDSTChanged && !isStart && isEndInDST;

  return showDSTMsgForStart || showDSTMsgForEnd ? (
    <div className={css.itemLabel}>
      <FormattedMessage id="OrderBreakdown.bookingWithDSTInEffect" />
    </div>
  ) : null;
};

const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone } = props;
  const intl = useIntl();

  const isDayType = dateType === DATE_TYPE_DATE;

  const fullDateFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  };

  const timeOnlyFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  const getTimeZoneName = date => {
    if (!timeZone || isDayType || !date) {
      return null;
    }
    try {
      const d = date instanceof Date ? date : new Date(date);
      const parts = new Intl.DateTimeFormat(intl?.locale || 'en-US', {
        timeZone,
        timeZoneName: 'short',
      }).formatToParts(d);
      return parts.find(p => p.type === 'timeZoneName')?.value || null;
    } catch (e) {
      return null;
    }
  };

  const startTimeZone = getTimeZoneName(startDate);
  const endTimeZone = getTimeZoneName(endDate);

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingStart" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...fullDateFormatOptions} />
          </div>
          {!isDayType ? (
            <div className={css.dayInfo}>
              <FormattedDate value={startDate} {...timeOnlyFormatOptions} />
              {startTimeZone ? ` ${startTimeZone}` : ''}
            </div>
          ) : null}
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={true}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>

        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingEnd" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...fullDateFormatOptions} />
          </div>
          {!isDayType ? (
            <div className={css.dayInfo}>
              <FormattedDate value={endDate} {...timeOnlyFormatOptions} />
              {endTimeZone ? ` ${endTimeZone}` : ''}
            </div>
          ) : null}
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={false}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>
      </div>
    </>
  );
};

/**
 * A line-item to show booking period for the OrderBreakdown
 *
 * @component
 * @param {Object} props
 * @param {propTypes.booking?} props.booking
 * @param {LINE_ITEM_NIGHT | LINE_ITEM_DAY | LINE_ITEM_HOUR} props.code
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {string} props.timeZone IANA time zone name
 * @returns {JSX.Element} line-item element for the order breakdown
 */
const LineItemBookingPeriod = props => {
  const { booking, code, dateType, timeZone } = props;

  if (!booking) {
    return null;
  }
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const bookingStartDate = displayStart || start;
  const bookingEndDateRaw = displayEnd || end;

  const isSingleDay =
    bookingStartDate instanceof Date &&
    bookingEndDateRaw instanceof Date &&
    isSameDay(bookingStartDate, bookingEndDateRaw, timeZone);

  const showInclusiveEndDate = [LINE_ITEM_DAY].includes(code) && !isSingleDay;
  const endDay = showInclusiveEndDate ? subtractTime(bookingEndDateRaw, 1, 'days') : bookingEndDateRaw;

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={bookingStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
          code={code}
        />
      </div>
      <hr className={css.totalDivider} />
    </>
  );
};

export default LineItemBookingPeriod;
